import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import "./DeviceSettings.css";
import "./DroneWaypoints.css";
import {
  fetchDroneWaypoints,
  ensureDronePath,
  saveWaypoint,
  deleteWaypoint,
} from "../../services/deviceService";
import WaypointMap from "./WaypointMap";
import Sidebar from "../shared/Sidebar";
import Header from "../shared/Header";
import LoadingSpinner from "../shared/LoadingSpinner";
import { useTheme } from "../../contexts/ThemeContext";
import { getItem, logout } from "../../utils/authStorage";
import { TablerX, TablerArrowLeft } from "../shared/Icons";
import { toast } from "react-toastify";

const DEFAULT_ALTITUDE = 50; // metres — must stay > 0 (RPi aborts mission on falsy altitude)
const DEFAULT_HOVER = 5; // seconds — must stay > 0 (RPi aborts on hover_duration <= 0)

function DroneWaypoints() {
  const { t } = useTranslation();
  const { deviceId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const deviceName = queryParams.get("name") || "";
  const { isDarkMode } = useTheme();

  const [collapsed, setCollapsed] = useState(false);
  const username = getItem("username");
  const role = (getItem("role") || "operator").toLowerCase();

  // Each point: { lat, lng, altitude, hover_duration }
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState("[]");
  const isDirty = JSON.stringify(points) !== savedSnapshot;

  const handleSignOut = () => logout();
  const hasRoleFn = (allowedRoles) => {
    const storedRole = (getItem("role") || "").toLowerCase();
    return allowedRoles.flat().some((r) => typeof r === "string" && r.toLowerCase() === storedRole);
  };

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const data = await fetchDroneWaypoints(deviceId);
        const list = Array.isArray(data) ? data : [];
        const loadedPoints = list.map((wp) => ({
          lat: parseFloat(wp.latitude),
          lng: parseFloat(wp.longitude),
          altitude: wp.altitude ?? DEFAULT_ALTITUDE,
          hover_duration: wp.hover_duration ?? DEFAULT_HOVER,
        }));
        setPoints(loadedPoints);
        setSavedSnapshot(JSON.stringify(loadedPoints));
      } catch (err) {
        console.error("Failed to fetch waypoints:", err);
        setPoints([]);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [deviceId]);

  const handleAddPoint = (latlng) => {
    setPoints((prev) => [
      ...prev,
      { lat: latlng.lat, lng: latlng.lng, altitude: DEFAULT_ALTITUDE, hover_duration: DEFAULT_HOVER },
    ]);
  };

  const handleUndo = () => setPoints((prev) => prev.slice(0, -1));
  const handleClear = () => setPoints([]);
  const handleRemove = (index) => setPoints((prev) => prev.filter((_, i) => i !== index));

  const handleFieldChange = (index, field, value) => {
    setPoints((prev) => prev.map((p, i) => (i === index ? { ...p, [field]: value } : p)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const pathId = await ensureDronePath(deviceId, deviceName ? `${deviceName} path` : null);
      // Reconcile: clear the path's existing waypoints, then re-create in current order.
      const existing = await fetchDroneWaypoints(deviceId);
      await Promise.all((Array.isArray(existing) ? existing : []).map((wp) => deleteWaypoint(wp.id)));
      for (let i = 0; i < points.length; i++) {
        const p = points[i];
        await saveWaypoint({
          latitude: Number(p.lat.toFixed(8)),
          longitude: Number(p.lng.toFixed(8)),
          altitude: p.altitude === "" ? DEFAULT_ALTITUDE : parseFloat(p.altitude),
          hover_duration: p.hover_duration === "" ? DEFAULT_HOVER : parseFloat(p.hover_duration),
          order: i,
          path: pathId,
        });
      }
      setSavedSnapshot(JSON.stringify(points));
      toast.success(t("label.waypointsSaved"));
    } catch (error) {
      console.error("Error saving waypoints:", error);
      toast.error(t("label.waypointSaveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const mapCenter = points.length ? [points[0].lat, points[0].lng] : undefined;

  return (
    <div className={`device-config-page ${isDarkMode ? "dark" : "light"}`}>
      <Sidebar
        username={username}
        onLogout={handleSignOut}
        hasRole={hasRoleFn}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((prev) => !prev)}
        isDarkMode={isDarkMode}
        role={role}
      />

      <div className="main-content">
        <Header title={t("label.deviceDetails")} />
        <div className="main-divider" />

        <div className="config-page-container">
          <div className="config-header-row">
            <div className="config-header-left">
              <button
                className="config-back-btn"
                onClick={() => navigate("/device-list")}
                title={t("button.backToList")}
                aria-label="Go Back"
              >
                <TablerArrowLeft size={16} />
              </button>
              <div className="title-area">
                <h3 className="config-title">{t("label.waypointsFor", { name: deviceName })}</h3>
                <p className="config-subtitle">{t("label.waypointsSubtitle")}</p>
              </div>
            </div>
            <div className="waypoint-toolbar">
              <button className="waypoint-btn" onClick={handleUndo} disabled={!points.length || saving}>
                {t("button.undoPoint")}
              </button>
              <button className="waypoint-btn" onClick={handleClear} disabled={!points.length || saving}>
                {t("button.clearPoints")}
              </button>
              <button className="add-config-btn" onClick={handleSave} disabled={saving || !points.length || !isDirty}>
                {saving ? t("button.saving") : t("button.savePath")}
              </button>
            </div>
          </div>

          {loading ? (
            <LoadingSpinner message={t("loading.configData")} />
          ) : (
            <div className="waypoint-layout">
              <div className="waypoint-map-wrapper">
                <WaypointMap points={points} onAdd={handleAddPoint} center={mapCenter} />
              </div>

              <div className="waypoint-list">
                <div className="waypoint-list-header">{t("label.flightOrder")}</div>
                {!points.length ? (
                  <div className="waypoint-empty">{t("label.clickMapToStart")}</div>
                ) : (
                  <ul className="waypoint-items">
                    {points.map((p, index) => (
                      <li className="waypoint-item" key={index}>
                        <span className={`waypoint-seq ${index === 0 ? "waypoint-seq-start" : ""}`}>
                          {index + 1}
                        </span>
                        <div className="waypoint-coords">
                          <span>{p.lat.toFixed(6)}</span>
                          <span>{p.lng.toFixed(6)}</span>
                        </div>
                        <div className="waypoint-inputs">
                          <label>
                            {t("label.altitude")}
                            <input
                              type="number"
                              step="any"
                              value={p.altitude}
                              onChange={(e) => handleFieldChange(index, "altitude", e.target.value)}
                            />
                          </label>
                          <label>
                            {t("label.hoverDuration")}
                            <input
                              type="number"
                              step="any"
                              value={p.hover_duration}
                              onChange={(e) => handleFieldChange(index, "hover_duration", e.target.value)}
                            />
                          </label>
                        </div>
                        <button
                          className="waypoint-remove"
                          onClick={() => handleRemove(index)}
                          title={t("button.deleteWaypoint")}
                        >
                          <TablerX size={15} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default DroneWaypoints;
